import { MoreHorizontal, Download, Pencil, FileText, FilePenLine, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { useInvoiceDelivery, CHANNELS } from "@/components/pawkt/InvoiceActions";

const EDITABLE = ["unpaid", "partially_paid", "paid"];

// Same eligibility rules the Invoices page buttons used (unchanged); backend re-checks all of them.
export const invoiceMenuRules = (inv, canEdit) => {
  const unpaidNoPay = inv.status === "unpaid" && Number(inv.amount_paid || 0) === 0;
  return {
    quickEdit: canEdit && unpaidNoPay && !(inv.items || []).some((it) => it.kind === "product") && Number(inv.coupon_discount || 0) === 0,
    reissue: canEdit && EDITABLE.includes(inv.status),
    correct: canEdit && EDITABLE.includes(inv.status),
    discard: canEdit && unpaidNoPay,
  };
};

const itemCls = "gap-2.5 text-[13px] cursor-pointer";
const labelCls = "px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400";

export function InvoiceRowMenu({ invoice, canEdit, onEdit, onReissue, onCorrect, onDiscard, testidSuffix = "" }) {
  const { downloadPdf, pdfBusy, openSend, dialog } = useInvoiceDelivery(invoice);
  const r = invoiceMenuRules(invoice, canEdit);
  const id = `${testidSuffix}${invoice.id}`;
  const hasEdit = r.quickEdit || r.reissue || r.correct;
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Actions for invoice ${invoice.invoice_number}`} data-testid={`invoice-menu-${id}`}
            className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-900">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" collisionPadding={8} className="w-52 bg-white" data-testid={`invoice-menu-content-${id}`}>
          <DropdownMenuLabel className={labelCls}>Invoice</DropdownMenuLabel>
          <DropdownMenuItem className={itemCls} disabled={pdfBusy} onSelect={downloadPdf} data-testid={`menu-pdf-${id}`}>
            <Download className="h-4 w-4 text-slate-500" /> {pdfBusy ? "Generating…" : "View / Download PDF"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className={labelCls}>Share</DropdownMenuLabel>
          <DropdownMenuGroup>
            {CHANNELS.map(({ key, label, icon: Icon }) => (
              <DropdownMenuItem key={key} className={itemCls} onSelect={() => openSend(key)} data-testid={`menu-${key}-${id}`}>
                <Icon className="h-4 w-4 text-slate-500" /> {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {hasEdit && (<>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className={labelCls}>Edit</DropdownMenuLabel>
            {r.quickEdit && <DropdownMenuItem className={itemCls} onSelect={() => onEdit(invoice)} data-testid={`menu-edit-${id}`}><Pencil className="h-4 w-4 text-slate-500" /> Edit</DropdownMenuItem>}
            {r.reissue && <DropdownMenuItem className={itemCls} onSelect={() => onReissue(invoice)} data-testid={`menu-reissue-${id}`}><FileText className="h-4 w-4 text-slate-500" /> Edit &amp; Reissue</DropdownMenuItem>}
            {r.correct && <DropdownMenuItem className={itemCls} onSelect={() => onCorrect(invoice)} data-testid={`menu-correct-${id}`}><FilePenLine className="h-4 w-4 text-slate-500" /> Correct Invoice</DropdownMenuItem>}
          </>)}
          {r.discard && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem className={`${itemCls} text-rose-600 focus:bg-rose-50 focus:text-rose-700`} onSelect={() => onDiscard(invoice)} data-testid={`menu-cancel-${id}`}>
              <Trash2 className="h-4 w-4" /> Cancel Invoice
            </DropdownMenuItem>
          </>)}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog}
    </>
  );
}
